import { AxiosResponse } from "axios";
import { call, fork, put, takeLatest } from "redux-saga/effects";

import { ImageFile } from "components/ImageUpload";
import { AssetItem } from "entities/assets";
import { ImageType } from "entities/common";
import { AssetImagesApi, AssetsApi } from "services/http";
import { getFetchSaga } from "store/commonSagas";
import {
  CreateAssetRequestPayload,
  UpdateAssetRequestPayload,
  createAssetFailure,
  createAssetRequest,
  createAssetSuccess,
  getAssetFailure,
  getAssetListFailure,
  getAssetListRequest,
  getAssetListSuccess,
  getAssetRequest,
  getAssetSuccess,
  removeAssetFailure,
  removeAssetRequest,
  removeAssetSuccess,
  updateAssetFailure,
  updateAssetRequest,
  updateAssetSuccess,
} from "store/slices/assetSlice";
import { showToast } from "store/slices/toastSlice";

const watchGetList = getFetchSaga(
  getAssetListRequest.type,
  AssetsApi.getList,
  getAssetListSuccess,
  getAssetListFailure,
);

const watchGetCurrent = getFetchSaga(getAssetRequest.type, AssetsApi.getCurrent, getAssetSuccess, getAssetFailure);

function* createAssetFlow(action: { type: string; payload: CreateAssetRequestPayload }) {
  const { body, images } = action.payload;

  try {
    const response: AxiosResponse<AssetItem> = yield call(AssetsApi.create, body);
    const asset = response.data;

    const filesToUpload = (images ?? [])
      .map((img: ImageFile) => img.file)
      .filter((file): file is File => Boolean(file));

    if (filesToUpload.length === 0) {
      yield put(createAssetSuccess(asset));
      return;
    }

    try {
      const uploadResponse: AxiosResponse<ImageType[]> = yield call(AssetImagesApi.upload, asset.id, filesToUpload);
      yield put(createAssetSuccess({ ...asset, images: uploadResponse.data }));
    } catch {
      yield put(createAssetSuccess(asset));
      yield put(
        showToast({
          id: `asset-image-upload-failed-${asset.id}`,
          message: "Asset created, but image upload failed.",
          type: "error",
        }),
      );
    }
  } catch (error) {
    yield put(createAssetFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: "error-creating-asset",
        message: "Failed to create asset. Please try again later.",
        type: "error",
        duration: 3000,
      }),
    );
  }
}

function* watchCreate() {
  yield takeLatest(createAssetRequest.type, createAssetFlow);
}

function* updateAssetFlow(action: { type: string; payload: UpdateAssetRequestPayload }) {
  const { id, body, newImages, removedImageIds } = action.payload;

  try {
    const response: AxiosResponse<AssetItem> = yield call(AssetsApi.update, id, body);
    let asset = response.data;

    const filesToUpload = (newImages ?? [])
      .map((img: ImageFile) => img.file)
      .filter((file): file is File => Boolean(file));
    const idsToRemove = removedImageIds ?? [];

    if (filesToUpload.length === 0 && idsToRemove.length === 0) {
      yield put(updateAssetSuccess(asset));
      return;
    }

    let imageOpsFailed = false;
    const successfullyRemovedIds: number[] = [];

    for (const imageId of idsToRemove) {
      try {
        yield call(AssetImagesApi.remove, id, imageId);
        successfullyRemovedIds.push(imageId);
      } catch {
        imageOpsFailed = true;
      }
    }

    let uploadedImages: ImageType[] = [];
    if (filesToUpload.length > 0) {
      try {
        const uploadResponse: AxiosResponse<ImageType[]> = yield call(AssetImagesApi.upload, id, filesToUpload);
        uploadedImages = uploadResponse.data;
      } catch {
        imageOpsFailed = true;
      }
    }

    const remainingExistingImages = (asset.images ?? []).filter((img) => !successfullyRemovedIds.includes(img.id));

    asset = {
      ...asset,
      images: [...remainingExistingImages, ...uploadedImages],
    };

    yield put(updateAssetSuccess(asset));

    if (imageOpsFailed) {
      yield put(
        showToast({
          id: `asset-image-update-failed-${id}`,
          message: "Asset updated, but some image changes failed.",
          type: "error",
        }),
      );
    }
  } catch (error) {
    yield put(updateAssetFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: "error-updating-asset",
        message: "Failed to update asset. Please try again later.",
        type: "error",
        duration: 3000,
      }),
    );
  }
}

function* watchUpdate() {
  yield takeLatest(updateAssetRequest.type, updateAssetFlow);
}

function* removeAssetFlow(action: { type: string; payload: number }) {
  try {
    yield call(AssetsApi.remove, action.payload);
    yield put(removeAssetSuccess(action.payload));
  } catch (error) {
    yield put(removeAssetFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: "error-removing-asset",
        message: "Failed to delete asset. Please try again later.",
        type: "error",
        duration: 3000,
      }),
    );
  }
}

function* watchRemove() {
  yield takeLatest(removeAssetRequest.type, removeAssetFlow);
}

export const assetWatchers = [
  fork(watchGetList),
  fork(watchGetCurrent),
  fork(watchCreate),
  fork(watchUpdate),
  fork(watchRemove),
];
